/*
 *  Copyright IBM Corp. 2025, 2026
 *
 *  This source code is licensed under the Apache-2.0 license found in the
 *  LICENSE file in the root directory of this source tree.
 *
 *  @license
 */

import React, { useMemo } from "react";
import Restart16 from "@carbon/icons/es/restart/16.js";
import cx from "classnames";

import ChatButton, {
  CHAT_BUTTON_KIND,
  CHAT_BUTTON_SIZE,
} from "../carbon/ChatButton";
import { ErrorMessage } from "../../components-legacy/ErrorMessage";
import { MarkdownWithDefaults } from "../util/MarkdownWithDefaults";
import { useCarbonTheme } from "../../hooks/useCarbonTheme";
import { carbonIconToReact } from "../../utils/carbonIcon";
import { LanguagePack } from "../../../types/config/PublicConfig";
import { useIntl } from "../../hooks/useIntl";

interface CatastrophicErrorPanelProps {
  assistantName: string;
  languagePack: LanguagePack;
  title?: string;
  bodyText?: string;
  onRestart: () => void;
  hideRetryButton?: boolean;
}

const CatastrophicErrorPanel: React.FC<CatastrophicErrorPanelProps> = ({
  assistantName,
  languagePack,
  title,
  bodyText,
  onRestart,
  hideRetryButton,
}) => {
  const intl = useIntl();
  const { isDarkTheme } = useCarbonTheme();

  const errorTitle = useMemo(
    () => title ?? languagePack.errors_somethingWrong,
    [title, languagePack],
  );

  const errorBodyText = useMemo(
    () =>
      bodyText ??
      intl.formatMessage(
        { id: "errors_communicating" as keyof LanguagePack },
        { assistantName },
      ),
    [bodyText, intl, assistantName],
  );

  const Restart = carbonIconToReact(Restart16);

  return (
    <div
      className={cx(
        "cds-custom-aichat--catastrophic-error",
        "cds-custom-aichat--panel-content",
      )}
    >
      <div className="cds-custom-aichat--catastrophic-error__error-text-container">
        <ErrorMessage theme={isDarkTheme ? "dark" : "light"} />
        <div className="cds-custom-aichat--catastrophic-error__error-heading">
          {errorTitle}
        </div>
        <div className="cds-custom-aichat--catastrophic-error__error-body">
          <MarkdownWithDefaults text={errorBodyText} highlight={true} />
        </div>
        {!hideRetryButton && onRestart && (
          <ChatButton
            className="cds-custom-aichat--catastrophic-error__restart-button"
            kind={CHAT_BUTTON_KIND.TERTIARY}
            size={CHAT_BUTTON_SIZE.SMALL}
            aria-label={languagePack.buttons_restart}
            onClick={onRestart}
          >
            <Restart slot="icon" />
            {languagePack.buttons_retry}
          </ChatButton>
        )}
      </div>
    </div>
  );
};

export default CatastrophicErrorPanel;
